import { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, ZoomIn, ZoomOut, Maximize2, FileText } from 'lucide-react';

// Configure pdf.js worker for Vite + react-pdf
// Use URL resolver so it always points at the installed pdfjs-dist version
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface ReactPDFPreviewProps {
  file?: File | null;
  url?: string | null;
  className?: string;
}

export function ReactPDFPreview({ file, url, className }: ReactPDFPreviewProps) {
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const src = useMemo(() => {
    if (file) return file;
    if (url) return url;
    return null;
  }, [file, url]);

  useEffect(() => {
    setError(null);
    setNumPages(null);
    setScale(1);
    setPage(1);
  }, [src]);

  const handleDownload = () => {
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.name || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } else if (typeof src === 'string') {
      window.open(src, '_blank');
    }
  };

  if (!src) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <p className="text-slate-500">No document to preview</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="p-3 border-b bg-slate-50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Bill Document</CardTitle>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
              className="h-7 w-7 p-0"
            >
              <ZoomOut className="h-3 w-3" />
            </Button>
            <span className="text-xs font-medium min-w-[45px] text-center">{Math.round(scale * 100)}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale((s) => Math.min(2, s + 0.1))}
              className="h-7 w-7 p-0"
            >
              <ZoomIn className="h-3 w-3" />
            </Button>
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <Button variant="outline" size="sm" onClick={handleDownload} className="h-7 px-2">
              <Download className="h-3 w-3 mr-1" />
              <span className="text-xs">Download</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative bg-slate-100" style={{ minHeight: '650px' }}>
          <div className="overflow-auto h-full p-4">
            <Document
              file={src}
              loading={<div className="p-8 text-center text-slate-600">Loading PDF…</div>}
              error={<div className="p-8 text-center text-red-600">Failed to load PDF</div>}
              onLoadSuccess={(pdf) => {
                setNumPages(pdf.numPages);
                setPage(1);
              }}
              onLoadError={(e) => setError(e?.message || 'Failed to load PDF')}
            >
              {!!numPages && (
                <div className="flex justify-center">
                  <Page pageNumber={page} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
                </div>
              )}
            </Document>

            {error && (
              <div className="flex flex-col items-center justify-center h-full py-16">
                <FileText className="h-16 w-16 text-slate-400 mb-4" />
                <p className="text-slate-600 mb-4">{error}</p>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            )}
          </div>
        </div>
        {/* Pagination footer */}
        <div className="p-3 border-t bg-slate-50 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!numPages || page <= 1}
          >
            Previous
          </Button>
          <span className="text-xs text-slate-700">
            Page {Math.min(page, numPages || 1)} of {numPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min((numPages || 1), p + 1))}
            disabled={!numPages || (page >= (numPages || 1))}
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
